import { Controller } from "@hotwired/stimulus"

const COOKIE_NAME = "klavy_cookie_consent"
const CONSENT_VERSION = 1
const ALL_CATEGORIES = ["essential", "preferences", "analytics", "marketing"]

export default class extends Controller {
  static targets = ["banner", "modal", "preferences", "analytics", "marketing"]

  connect() {
    this.handleTriggerClick = this.handleTriggerClick.bind(this)
    document.addEventListener("click", this.handleTriggerClick)

    const consent = this.readConsent()
    this.publishConsent(consent)

    if (consent || this.isSessionDismissed()) {
      this.hideBanner()
    } else {
      this.showBanner()
    }
  }

  disconnect() {
    document.removeEventListener("click", this.handleTriggerClick)
  }

  acceptAll() {
    this.saveConsent({
      essential: true,
      preferences: true,
      analytics: true,
      marketing: true
    })
  }

  acceptEssential() {
    this.rejectAll()
  }

  rejectAll() {
    this.saveConsent({
      essential: true,
      preferences: false,
      analytics: false,
      marketing: false
    })
  }

  openSettings() {
    const consent = this.readConsent()
    const categories = consent?.categories || {}

    this.preferencesTarget.checked = categories.preferences === true
    this.analyticsTarget.checked = categories.analytics === true
    this.marketingTarget.checked = categories.marketing === true

    this.modalTarget.hidden = false
    this.modalTarget.setAttribute("aria-hidden", "false")
    document.documentElement.style.overflow = "hidden"
  }

  closeSettings() {
    this.modalTarget.hidden = true
    this.modalTarget.setAttribute("aria-hidden", "true")
    document.documentElement.style.overflow = ""
  }

  saveSettings() {
    this.saveConsent({
      essential: true,
      preferences: this.preferencesTarget.checked,
      analytics: this.analyticsTarget.checked,
      marketing: this.marketingTarget.checked
    })
  }

  dismiss() {
    this.setSessionDismissed()
    this.hideBanner()
  }

  backdropClose(event) {
    if (event.target === this.modalTarget) this.closeSettings()
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest("[data-cookie-settings-open]")
    if (!trigger) return

    event.preventDefault()
    this.openSettings()
  }

  saveConsent(categories) {
    const payload = {
      version: CONSENT_VERSION,
      categories: this.normalizeCategories(categories),
      decided_at: new Date().toISOString(),
      locale: document.documentElement.lang || "ko"
    }

    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}; Max-Age=31536000; Path=/; SameSite=Lax${secure}`
    this.clearSessionDismissed()
    this.publishConsent(payload)
    this.hideBanner()
    this.closeSettings()
  }

  readConsent() {
    const raw = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${COOKIE_NAME}=`))
      ?.split("=")[1]

    if (!raw) return null

    try {
      const parsed = JSON.parse(decodeURIComponent(raw))
      if (parsed.version !== CONSENT_VERSION) return null
      return {
        ...parsed,
        categories: this.normalizeCategories(parsed.categories || {})
      }
    } catch (_error) {
      return null
    }
  }

  publishConsent(consent) {
    const categories = this.normalizeCategories(consent?.categories || { essential: true })
    window.KLAVY_COOKIE_CONSENT = categories
    window.dispatchEvent(new CustomEvent("klavy:cookie-consent", { detail: categories }))
  }

  normalizeCategories(categories) {
    return ALL_CATEGORIES.reduce((normalized, category) => {
      normalized[category] = category === "essential" ? true : categories[category] === true
      return normalized
    }, {})
  }

  showBanner() {
    this.bannerTarget.hidden = false
  }

  hideBanner() {
    this.bannerTarget.hidden = true
  }

  isSessionDismissed() {
    try {
      return sessionStorage.getItem("klavy_cookie_consent_dismissed") === "true"
    } catch (_error) {
      return false
    }
  }

  setSessionDismissed() {
    try {
      sessionStorage.setItem("klavy_cookie_consent_dismissed", "true")
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes. The banner will return next navigation.
    }
  }

  clearSessionDismissed() {
    try {
      sessionStorage.removeItem("klavy_cookie_consent_dismissed")
    } catch (_error) {
      // No-op when session storage is unavailable.
    }
  }
}
